import type { DateTime } from "luxon";
import type { CustomTimeGroupsConfig } from "../../utils/models";
import {
	bucketRowsByCustomTimeGroup,
	configuredGroupKey,
	customTimeGroupKeyLabel,
	formatCustomTimeGroupRange,
	resolveActiveCustomTimeGroups,
	shiftBelongsToCustomTimeGroupKey,
	UNMATCHED_GROUP_KEY,
} from "../dailyPrintout/layout/customTimeGroups";
import type { AiPrintoutTimeGroup } from "./contract";

/** The day's time groups, and which of them a time range belongs to. */
export type AiPrintoutTimeGroups = {
	groups: AiPrintoutTimeGroup[];
	keysOf: (start: DateTime, end: DateTime) => string[];
};

interface ResolveTimeGroupsArgs {
	config: CustomTimeGroupsConfig;
	timezone: string;
	dayStart: string;
	otherLabel: string;
}

/**
 * The viewer's custom shift time groups, as the Detailed printout's Start Time sections use them: the configured
 * groups in time order from the day start (`bucketRowsByCustomTimeGroup`'s order), then Other. A range belongs to
 * every group it covers by an hour, and to Other when it covers none (`shiftBelongsToCustomTimeGroupKey`). Null
 * when the switch is off.
 */
export const resolveTimeGroups = ({ config, timezone, dayStart, otherLabel }: ResolveTimeGroupsArgs): AiPrintoutTimeGroups | null => {
	const active = resolveActiveCustomTimeGroups({ config, timezone, dayStart, otherLabel });
	if (active === null) return null;
	const configuredByKey = new Map(active.groups.map((group) => [configuredGroupKey(group), group]));
	const orderedKeys = [
		...bucketRowsByCustomTimeGroup({ rows: [], groups: active.groups, timezone, dayStart, sortDirection: "asc" }).map((bucket) => bucket.key),
		UNMATCHED_GROUP_KEY,
	];
	const groups = orderedKeys.map((key): AiPrintoutTimeGroup => {
		const configured = configuredByKey.get(key);
		return {
			key,
			label: customTimeGroupKeyLabel(key, active.labels),
			timeLabel: configured === undefined ? null : formatCustomTimeGroupRange(configured),
			isOther: key === UNMATCHED_GROUP_KEY,
		};
	});
	return {
		groups,
		keysOf: (start, end) =>
			orderedKeys.filter((key) => shiftBelongsToCustomTimeGroupKey({ shiftStart: start, shiftEnd: end, key, groups: active.groups, timezone })),
	};
};

/** Which time groups a time range belongs to; null for every range when there are none (the switch is off). */
export type TimeGroupKeysOf = (start: DateTime, end: DateTime) => string[] | null;

export const timeGroupKeysOf =
	(timeGroups: AiPrintoutTimeGroups | null): TimeGroupKeysOf =>
	(start, end) =>
		timeGroups === null ? null : timeGroups.keysOf(start, end);
